<template>
  <div class="property-movement-card">
    <!-- Property Header -->
    <div class="property-header">
      <div class="property-title">
        <h4>{{ property.property_description || '-' }}</h4>
        <p class="property-desc">{{ property.property_type?.property_type || property.PropertyType?.property_type || 'Unknown Type' }}</p>
      </div>
    </div>

    <!-- Current Location -->
    <div class="location-breakdown">
      <h5>Current Location: <span class="current-location-value">{{ currentLocationLabel }}</span></h5>
    </div>

    <!-- Action Buttons (hidden on View; shown on Edit table) -->
    <div v-if="showMovementActionButtons" class="action-buttons">
      <button
        @click="$emit('send-fsl')"
        :disabled="!canSendToFSL"
        class="btn btn-primary"
      >
        <i class="ri-send-plane-line"></i> Send to FSL
      </button>
      <button
        @click="$emit('receive-fsl')"
        :disabled="!hasPendingFSL"
        class="btn btn-success"
      >
        <i class="ri-inbox-line"></i> Receive from FSL
      </button>
      <button
        @click="$emit('send-court')"
        :disabled="!canSendToCourt"
        class="btn btn-info"
      >
        <i class="ri-government-line"></i> Send to Court
      </button>
      <button
        @click="$emit('destroy')"
        :disabled="!canDestroy"
        class="btn btn-danger"
      >
        <i class="ri-delete-bin-line"></i> Destroy
      </button>
      <button
        @click="$emit('update-location')"
        class="btn btn-secondary"
      >
        <i class="ri-map-pin-line"></i> Update Location
      </button>
    </div>

    <!-- Movement Timeline -->
    <div v-if="movements && movements.length > 0" class="movement-timeline">
      <h5>Movement History</h5>
      <div class="timeline">
        <div
          v-for="movement in movements"
          :key="movement.movement_id"
          class="timeline-item"
          :class="'timeline-' + movement.type"
        >
          <div class="timeline-icon">
            <i :class="getMovementIcon(movement.type)"></i>
          </div>
          <div class="timeline-content">
            <div class="timeline-header">
              <strong>{{ movement.type_label }}</strong>
              <span class="timeline-date">{{ movement.movement_date }}</span>
            </div>
            <div v-if="getMovementMetaLine(movement)" class="timeline-details">
              {{ getMovementMetaLine(movement) }}
            </div>
            <div v-if="movement.remarks" class="timeline-remarks">
              <em>{{ movement.remarks }}</em>
            </div>
            <div class="timeline-user">
              By: {{ movement.created_by }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PropertyMovementCard',
  props: {
    property: {
      type: Object,
      required: true
    },
    locationBreakdown: {
      type: Array,
      default: () => []
    },
    movements: {
      type: Array,
      default: () => []
    },
    fslBatches: {
      type: Array,
      default: () => []
    },
    totalQuantity: {
      type: Number,
      default: 0
    },
    showSendToFSLCourtButtons: {
      type: Boolean,
      default: false
    },
    showMovementActionButtons: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    canSendToFSL() {
      const psLoc = this.locationBreakdown.find(l => l.type === 'ps');
      return psLoc && psLoc.quantity > 0;
    },
    hasPendingFSL() {
      return this.fslBatches && this.fslBatches.length > 0;
    },
    canSendToCourt() {
      const psLoc = this.locationBreakdown.find(l => l.type === 'ps');
      return psLoc && psLoc.quantity > 0;
    },
    canDestroy() {
      const psLoc = this.locationBreakdown.find(l => l.type === 'ps');
      return psLoc && psLoc.quantity > 0;
    },
    currentLocationLabel() {
      const getQty = (type) => Number(this.locationBreakdown.find((l) => l.type === type)?.quantity || 0);
      if (getQty('fsl') > 0) return 'FSL';
      if (getQty('court') > 0) return 'Court';
      if (getQty('ps') > 0) return 'Police Station';
      if (getQty('destroyed') > 0) return 'Destroyed';
      if (getQty('others') > 0) return 'Others';
      return '—';
    }
  },
  methods: {
    formatQuantity(qty) {
      if (!qty) return '0 g';
      if (qty >= 1000) {
        return `${(qty / 1000).toFixed(3)} kg`;
      }
      return `${qty.toFixed(3)} g`;
    },
    getLocationIcon(type) {
      const icons = {
        ps: 'ri-building-line',
        fsl: 'ri-flask-line',
        court: 'ri-government-line',
        destroyed: 'ri-delete-bin-line',
        others: 'ri-map-pin-line'
      };
      return icons[type] || 'ri-question-line';
    },
    getMovementIcon(type) {
      const icons = {
        fsl_send: 'ri-send-plane-fill',
        fsl_receive: 'ri-inbox-fill',
        court_send: 'ri-government-fill',
        court_receive: 'ri-inbox-fill',
        destruction: 'ri-delete-bin-fill',
        location_change: 'ri-map-pin-fill'
      };
      return icons[type] || 'ri-arrow-right-line';
    },
    getMovementMetaLine(movement) {
      if (!movement) return '';
      if (movement.type === 'fsl_send') {
        const ack = movement.fsl_detail?.acknowledgement_no;
        return ack ? `Ack No: ${ack}` : '';
      }
      if (movement.type === 'court_send') {
        const pr = movement.court_submission?.pr_number;
        return pr ? `PR No: ${pr}` : '';
      }
      return '';
    }
  }
}
</script>

<style scoped>
.property-movement-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  background: #fff;
}

.property-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

.property-title h4 {
  margin: 0 0 5px 0;
  color: #333;
}

.property-desc {
  margin: 0;
  color: #666;
  font-size: 14px;
  text-transform: uppercase;
}

.location-breakdown h5 {
  margin: 0 0 14px 0;
  font-size: 14px;
  color: #666;
  text-transform: uppercase;
}

.current-location-value {
  color: #333;
  font-weight: 700;
  text-transform: none;
  margin-left: 6px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.btn {
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary { background: #2196F3; color: white; }
.btn-primary:hover:not(:disabled) { background: #1976D2; }
.btn-success { background: #4CAF50; color: white; }
.btn-success:hover:not(:disabled) { background: #388E3C; }
.btn-info { background: #9C27B0; color: white; }
.btn-info:hover:not(:disabled) { background: #7B1FA2; }
.btn-danger { background: #F44336; color: white; }
.btn-danger:hover:not(:disabled) { background: #D32F2F; }
.btn-secondary { background: #607D8B; color: white; }
.btn-secondary:hover:not(:disabled) { background: #455A64; }

.movement-timeline {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #f0f0f0;
}

.movement-timeline h5 {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #666;
  text-transform: uppercase;
}

.timeline {
  position: relative;
  padding-left: 40px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e0e0e0;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-icon {
  position: absolute;
  left: -40px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #2196F3;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2196F3;
}

.timeline-fsl_send .timeline-icon,
.timeline-court_send .timeline-icon {
  border-color: #FF9800;
  color: #FF9800;
}

.timeline-fsl_receive .timeline-icon,
.timeline-court_receive .timeline-icon {
  border-color: #4CAF50;
  color: #4CAF50;
}

.timeline-destruction .timeline-icon {
  border-color: #F44336;
  color: #F44336;
}

.timeline-content {
  background: #f9f9f9;
  padding: 12px;
  border-radius: 6px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.timeline-header strong {
  color: #333;
}

.timeline-date {
  font-size: 12px;
  color: #666;
}

.timeline-details {
  color: #555;
  margin-bottom: 6px;
}

.timeline-remarks {
  color: #777;
  font-size: 13px;
  margin-bottom: 6px;
}

.timeline-user {
  font-size: 12px;
  color: #999;
}
</style>
